import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import PublicPage from '../components/public/PublicPage';
import usePageTitle from '../hooks/usePageTitle';
import { ROUTES } from '../utils/constants';
import { FiArrowUpRight, FiArrowRight, FiCheck, FiShield, FiHome, FiFileText, FiLock, FiUserCheck } from 'react-icons/fi';
import './LandingPage.css';

const STEPS = [
  { title: 'Share your property link', text: 'Add a property and share its application link or QR code with prospective tenants.' },
  { title: 'Applicants consent and submit', text: 'Applicants see the requested checks, give consent and submit their details, documents and references.' },
  { title: 'Review and decide', text: 'Review the findings in one place and record your own decision with reasons. Applicants can ask for corrections or raise a dispute.' }
];
const LANDLORD_GETS = [
  'Collect applications through one link for each property.',
  'Compare applicants and review supporting findings in one place.',
  'Keep your team organised with shared progress and assigned roles.',
  'Record rental decisions and the reasons behind them.'
];
const APPLICANT_GETS = [
  'Understand what is requested before you share your information.',
  'Apply through your personal email link and save your progress.',
  'Follow your application and review your consent history.',
  'Ask for a correction, raise a dispute or request your data.'
];
const PRINCIPLES = [
  { title: 'Permission before checks', text: 'Consent is linked to a specific property application. Applicants can review what they agreed to and manage their consent.' },
  { title: 'A decision you can explain', text: 'Findings support your assessment. The landlord or agent makes the decision and records their reasons.' },
  { title: 'Room to put things right', text: 'Applicants can ask for inaccurate information to be corrected and raise a dispute about the findings.' }
];

const LandingPage = () => {
  const [journey, setJourney] = useState('landlord');
  usePageTitle(
    '',
    'TenantVerify SA helps South African landlords and agents verify rental applicants with the applicant’s informed consent, and gives applicants access, correction and dispute rights.'
  );

  // Links such as /#pricing scroll to their section, from any page.
  const { hash } = useLocation();
  useEffect(() => {
    // Jump rather than animate: it always lands on the section, and suits people who prefer reduced motion.
    if (!hash) {
      window.scrollTo({ top: 0, behavior: 'instant' });
      return;
    }
    const target = document.getElementById(hash.slice(1));
    if (target) target.scrollIntoView({ behavior: 'instant', block: 'start' });
  }, [hash]);

  return (
    <PublicPage className="landing">
      <section className="landing-hero landing-hero--photo">
        <img className="landing-hero__photo" src={process.env.PUBLIC_URL + "/brand/rental-people.jpg"} alt="" width="1536" height="1024" fetchPriority="high" />
        <div className="public-container landing-hero__inner">
          <div className="landing-hero__copy">
          <p className="landing-eyebrow">Tenant verification, made clearer</p>
          <h1>Know more.<br />Rent with<br /><em>confidence.</em></h1>
          <p className="landing-lead">
            Tenant verification for South African landlords and agents. Collect rental applications, review supporting information and make informed decisions — with applicant consent at every step.
          </p>
          <div className="landing-hero__actions">
            <Link to="/#get-started" className="public-btn public-btn--primary">Get started <FiArrowUpRight /></Link>
            <Link to={{ pathname: '/', hash: '#how-it-works' }} className="landing-hero__link">See how it works <FiArrowRight /></Link>
          </div>
          <p className="landing-hero__note"><FiLock /> Free for applicants. Consent before checks.</p>
          <Link to="/#applicants" className="landing-applicant-link">Applying for a home? Start here <FiArrowRight /></Link>
          </div>
          <aside id="get-started" className="landing-start" aria-labelledby="start-title">
            <span className="landing-start__icon"><FiShield aria-hidden="true" /></span>
            <h2 id="start-title">Your next rental.<br />A clearer starting point.</h2>
            <p>Choose how you’re using TenantVerify.</p>
            <fieldset>
              <legend>I’m here as a</legend>
              <label><input type="radio" name="journey" value="landlord" checked={journey === 'landlord'} onChange={() => setJourney('landlord')} /><span>Landlord or agent<small>Manage properties and applications</small></span></label>
              <label><input type="radio" name="journey" value="applicant" checked={journey === 'applicant'} onChange={() => setJourney('applicant')} /><span>Rental applicant<small>Apply for a place to call home</small></span></label>
            </fieldset>
            <div className="landing-start__guidance" aria-live="polite">{journey === 'landlord' ? 'Create an account, add your property and invite applicants. View pricing before choosing your verification options.' : 'Use the property link or QR code from your landlord or agent. Already started? Return through the personal link in your email.'}</div>
            <Link className="public-btn public-btn--light" to={journey === 'landlord' ? '/register?role=landlord' : '/#applicants'}>{journey === 'landlord' ? 'Create your account' : 'View applicant guidance'} <FiArrowRight aria-hidden="true" /></Link>
            <p className="landing-start__footer">{journey === 'landlord' ? <>Already registered? <Link to="/login">Log in</Link></> : <>Need your invitation? <Link to="/contact">Get help</Link></>}</p>
          </aside>
        </div>
      </section>

      <section className="verification-strip" aria-label="Rental verification areas">
        <div className="public-container"><span>One application. A fuller picture.</span><span><FiUserCheck /> Identity</span><span><FiFileText /> Income & affordability</span><span><FiHome /> Rental references</span><span><FiShield /> Applicant consent</span></div>
      </section>

      <section className="landing-section" aria-labelledby="problem-title">
        <div className="public-container landing-story">
          <div className="report-scene" aria-label="Illustrative verification report, example data">
            <div className="report-preview">
              <div className="report-preview__top"><span><FiFileText /> Verification overview</span><small>EXAMPLE REPORT</small></div>
              <div className="report-property"><span className="report-property__icon"><FiHome /></span><div><small>RENTAL APPLICATION</small><h2>Your next tenancy</h2><p>One property. All the right information.</p></div></div>
              <div className="report-applicant"><span className="report-avatar">TM</span><div><strong>Thabo M.</strong><small>Illustrative applicant</small></div><span className="report-consent"><FiCheck /> Consent given</span></div>
              <div className="report-checks">
                {[['Identity', 'Verified'], ['Income & affordability', 'Reviewed'], ['Employer reference', 'Received'], ['Landlord reference', 'Received']].map(([name, status]) => <div key={name}><span><FiCheck />{name}</span><strong>{status}</strong></div>)}
              </div>
              <div className="report-decision"><FiUserCheck /><div><strong>The evidence is here. The decision is yours.</strong><p>A person makes every rental decision.</p></div></div>
            </div>
          </div>
          <div>
            <p className="landing-eyebrow">What you get</p>
            <h2 id="problem-title">The information you need.<br />Together in one report.</h2>
            <p>Bring the application and its supporting findings into one view, so you can understand what has been reviewed and what still needs attention.</p>
            <ul className="landing-report-benefits"><li><FiCheck /> Identity and submitted income findings</li><li><FiCheck /> Affordability in the context of the proposed rent</li><li><FiCheck /> Employer and previous-landlord references</li><li><FiCheck /> Check statuses and items requiring review</li><li><FiCheck /> A recorded decision with your reasons</li></ul>
            <p className="landing-report-note">Checks depend on consent and availability. Credit information, where available, comes from an authorised provider.</p>
                        <Link to="/#how-it-works" className="landing-hero__link">Explore the process <FiArrowRight /></Link>
          </div>
        </div>
      </section>

      <section id="how-it-works" className="landing-section landing-section--alt" aria-labelledby="how-title">
        <div className="public-container">
          <p className="landing-eyebrow">A simple path forward</p>
          <h2 id="how-title">One application. Three clear steps.</h2>
          <ol className="landing-steps">
            {STEPS.map(({ title, text }, i) => (
              <li key={title} className="landing-step">
                <span className="landing-step__num" aria-hidden="true">{i + 1}</span>
                <div>
                  <h3>{title}</h3>
                  <p>{text}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="who-its-for" className="landing-section" aria-labelledby="gets-title">
        <div className="public-container">
          <p className="landing-eyebrow">Better for both sides</p>
          <h2 id="gets-title">Confidence for landlords.<br />Clarity for applicants.</h2>
          <div className="landing-grid landing-grid--2">
            <article className="landing-card">
              <h3>Landlords and agents</h3>
              <ul className="landing-checks">
                {LANDLORD_GETS.map((item) => <li key={item}>{item}</li>)}
              </ul>
            </article>
            <article className="landing-card">
              <h3 id="applicants">Rental applicants</h3>
              <ul className="landing-checks">
                {APPLICANT_GETS.map((item) => <li key={item}>{item}</li>)}
              </ul>
              <p className="landing-applicant-help">Ready to apply? Open the property link or QR code from your landlord or agent. Returning applicants can use their personal email link.</p>
            </article>
          </div>
        </div>
      </section>

      <section id="trust" className="landing-section landing-section--alt" aria-labelledby="trust-title">
        <div className="public-container">
          <h2 id="trust-title">Built on trust and privacy</h2>
          <p className="landing-sub">
            Understand how your information is handled. Read our <Link to="/privacy">Privacy Policy</Link> and the{' '}
            <Link to="/terms">Terms and Conditions</Link> for the detail, or <Link to="/about">learn more about us</Link>.
          </p>
          <div className="landing-grid landing-grid--3">
            {PRINCIPLES.map(({ title, text }) => (
              <article key={title} className="landing-card">
                <h3>{title}</h3>
                <p>{text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>
      <section className="landing-cta" aria-labelledby="ready-title">
        <div className="public-container">
          <p className="landing-eyebrow">Take the next step</p>
          <h2 id="ready-title">Make your next rental decision an informed one.</h2>
          <p>Start with a property. Bring the application, consent and findings together. Keep the final decision in your hands.</p>
          <div className="landing-hero__actions"><Link to="/register?role=landlord" className="public-btn public-btn--light">Create your account <FiArrowUpRight /></Link><Link to="/pricing" className="landing-cta__link">View pricing</Link></div>
          <p className="landing-cta__more">Questions before you begin? <Link to="/faq">Read the FAQ</Link> or <Link to="/contact">talk to us</Link>.</p>
        </div>
      </section>
    </PublicPage>
  );
};

export default LandingPage;
